import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Package, Loader2, Check, X, RefreshCw, AlertCircle, ShieldCheck } from "lucide-react";
import {
  getAdminKits, approveAdminKit, rejectAdminKit, AdminKit, AdminKitCounts,
} from "../../api/services/adminKitService";
import { getErrorMessage } from "@/utils/errorUtils";

const STATUS_TABS = ["pending", "active", "disabled", "all"] as const;
type Tab = typeof STATUS_TABS[number];

const isPaid = (s: string) =>
  ["completed", "paid", "success", "successful", "confirmed", "waived"].includes(String(s || "").toLowerCase());

function StatusBadge({ value }: { value: string }) {
  const { t } = useTranslation();
  const v = String(value || "").toLowerCase();
  const map: Record<string, string> = {
    active: "bg-green-100 text-green-700",
    pending: "bg-amber-100 text-amber-700",
    disabled: "bg-red-100 text-red-700",
    maintenance: "bg-gray-200 text-gray-700",
    completed: "bg-green-100 text-green-700",
    "not-required": "bg-gray-100 text-gray-600",
    verified: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
  };
  const cls = map[v] || "bg-gray-100 text-gray-600";
  const defaultLabel = v ? v.charAt(0).toUpperCase() + v.slice(1) : "—";
  const label = t(`admin.kits.status.${v}`, defaultLabel);
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`}>{label}</span>;
}

export default function AdminKits() {
  const { t } = useTranslation();
  const [kits, setKits] = useState<AdminKit[]>([]);
  const [counts, setCounts] = useState<AdminKitCounts>({ total: 0, pending: 0, active: 0, disabled: 0, maintenance: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [tab, setTab] = useState<Tab>("pending");
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { kits, counts } = await getAdminKits(tab);
      setKits(kits); setCounts(counts);
    } catch (e: unknown) {
      setErr(getErrorMessage(e) || "Failed to load kits");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(""), 4000); };

  const approve = async (k: AdminKit) => {
    setBusyId(k.id); setErr("");
    try {
      const r = await approveAdminKit(k.id);
      flash(r.message || "Kit approved and activated.");
      await load();
    } catch (e: unknown) {
      setErr(getErrorMessage(e) || "Failed to approve kit");
    } finally { setBusyId(null); }
  };

  const reject = async (k: AdminKit) => {
    setBusyId(k.id); setErr("");
    try {
      const r = await rejectAdminKit(k.id);
      flash(r.message || "Kit rejected.");
      await load();
    } catch (e: unknown) {
      setErr(getErrorMessage(e) || "Failed to reject kit");
    } finally { setBusyId(null); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold flex items-center gap-2 dark:text-white">
          <Package className="w-6 h-6 text-blue-600" /> {t("admin.kits.title", "Kit Approvals")}
        </h1>
        <button onClick={load} className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
          <RefreshCw className="w-4 h-4" /> {t("admin.kits.button.refresh", "Refresh")}
        </button>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {t("admin.kits.subtitle", "Review operator-registered (BYO) kits and approve paid ones to activate them.")}
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label: t("admin.kits.stats.total", "Total"), val: counts.total },
          { label: t("admin.kits.stats.pending", "Pending"), val: counts.pending },
          { label: t("admin.kits.stats.active", "Active"), val: counts.active },
          { label: t("admin.kits.stats.disabled", "Disabled"), val: counts.disabled },
        ].map((c) => (
          <Card key={c.label} className="p-4">
            <div className="text-2xl font-bold dark:text-white">{c.val}</div>
            <div className="text-xs text-gray-500">{c.label}</div>
          </Card>
        ))}
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {STATUS_TABS.map((tabStr) => (
          <button key={tabStr} onClick={() => setTab(tabStr)}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${
              tab === tabStr ? "bg-blue-600 text-white" : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            }`}>
            {t(`admin.kits.tabs.${tabStr}`, tabStr)}
          </button>
        ))}
      </div>

      {msg && (
        <div className="mb-4 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
          <ShieldCheck className="w-4 h-4" /> {msg}
        </div>
      )}
      {err && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>
      ) : kits.length === 0 ? (
        <Card className="p-10 text-center text-gray-500">{t("admin.kits.noKits", "No kits in this view.")}</Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b dark:border-gray-700">
                <th className="p-3 font-semibold">{t("admin.kits.table.kitDevice", "Kit / Device")}</th>
                <th className="p-3 font-semibold">{t("admin.kits.table.operator", "Operator")}</th>
                <th className="p-3 font-semibold">{t("admin.kits.table.type", "Type")}</th>
                <th className="p-3 font-semibold">{t("admin.kits.table.status", "Status")}</th>
                <th className="p-3 font-semibold">{t("admin.kits.table.payment", "Payment")}</th>
                <th className="p-3 font-semibold">{t("admin.kits.table.kyc", "KYC")}</th>
                <th className="p-3 font-semibold text-right">{t("admin.kits.table.action", "Action")}</th>
              </tr>
            </thead>
            <tbody>
              {kits.map((k) => {
                const paid = isPaid(k.paymentStatus);
                const pending = k.status === "pending";
                return (
                  <tr key={k.id} className="border-b dark:border-gray-800 last:border-0">
                    <td className="p-3">
                      <div className="font-semibold dark:text-white">{k.name}</div>
                      <div className="text-xs text-gray-500">
                        {[
                          k.deviceBrand === "Pending Configuration" ? t("admin.kits.device.pending", "Pending Configuration") : k.deviceBrand,
                          k.deviceModel === "Pending Configuration" ? t("admin.kits.device.pending", "Pending Configuration") : k.deviceModel
                        ].filter(Boolean).join(" ")}
                        {k.serialNumber ? ` · ${k.serialNumber}` : ""}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="dark:text-gray-200">{k.operator?.name || "—"}</div>
                      <div className="text-xs text-gray-500">{k.operator?.email || ""}</div>
                    </td>
                    <td className="p-3 capitalize dark:text-gray-300">{k.kitType}</td>
                    <td className="p-3"><StatusBadge value={k.status} /></td>
                    <td className="p-3"><StatusBadge value={k.paymentStatus} /></td>
                    <td className="p-3"><StatusBadge value={k.kycStatus} /></td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-2">
                        {pending ? (
                          <>
                            <button
                              onClick={() => approve(k)}
                              disabled={!paid || busyId === k.id}
                              title={!paid ? "Payment not confirmed — cannot activate" : "Approve & activate"}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-600 text-white disabled:opacity-40 disabled:cursor-not-allowed">
                              {busyId === k.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                              {t("admin.kits.button.approve", "Approve")}
                            </button>
                            <button
                              onClick={() => reject(k)}
                              disabled={busyId === k.id}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-100 text-red-700 disabled:opacity-40">
                              <X className="w-3.5 h-3.5" /> {t("admin.kits.button.reject", "Reject")}
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
